import { apiSlice } from './apiSlice';

export type GovernanceOverviewResponse = {
  generatedAt: string;
  governanceDictionaryVersion: string;
  workflowMetrics: {
    overdueTasks: number;
    criticalOverdueTasks: number;
    staleWorkflows: number;
    orphanWorkflowWarnings: number;
  };
  financeGovernance: {
    reconciliationAnomalies: number;
    profitabilityMismatchCount: number;
    paymentDriftCount: number;
  };
  operationalHealth: {
    failedJobs: number;
    staleSchedulerWarnings: number;
    schedulerRunning: {
      financeOverdue: boolean;
      controlTaskOverdue: boolean;
    };
  };
  operationalLoad: {
    accountantBacklog: number;
    logistBacklog: number;
    unresolvedRouteIssues: number;
    backlogSeverity: 'INFO' | 'WARNING' | 'CRITICAL' | string;
  };
  slaSummary: {
    healthy: number;
    nearBreach: number;
    breached: number;
    criticalBreach: number;
    breachedTotal: number;
  };
  escalationSummary: {
    breachedSlaCount: number;
    criticalBreachCount: number;
    financeEscalationWarnings: number;
    routeDelayEscalationWarnings: number;
    backlogEscalationWarnings: number;
    escalationPressure: 'NORMAL' | 'ELEVATED' | 'HIGH' | string;
    topOperationalRisks: string[];
  };
  pressure: {
    level: 'NORMAL' | 'ELEVATED' | 'HIGH' | 'CRITICAL' | string;
    score: number;
    contributors: {
      criticalOverdueTasks: number;
      failedJobs: number;
      financeAnomalies: number;
      unresolvedRouteIssues: number;
      staleWorkflows: number;
    };
  };
  queueAging: {
    tasks: Array<{
      bucket: 'TODAY' | 'DAYS_1_3' | 'MORE_THAN_3_DAYS' | 'OVERDUE' | 'CRITICAL_DELAY' | string;
      label: string;
      count: number;
    }>;
  };
  workflowStall: {
    stalledByDocuments: number;
    stalledFinanceApprovals: number;
    staleWorkflowCount: number;
    isQueueShrinkingRisk: boolean;
  };
  completionVisibility: {
    physicallyCompleted: number;
    fullyClosed: number;
    physicallyCompletedButNotClosed: number;
  };
  blockerReasons: Array<{
    reasonCode: string;
    reason: string;
    severity: 'INFO' | 'WARNING' | 'CRITICAL' | string;
    owner: string;
    nextAction: string;
    blockerCategory: string;
    affectedWorkflow?: string;
    affectedEntity?: string;
    count: number;
    aging: {
      bucket: 'TODAY' | 'DAYS_1_3' | 'MORE_THAN_3_DAYS' | 'OVERDUE' | 'CRITICAL_DELAY' | string;
      label: string;
    };
    urgencyScore: number;
    priorityLevel: 'LOW' | 'ATTENTION' | 'HIGH' | 'CRITICAL' | string;
    priorityLabel: string;
    operationalImpact: Array<{
      type: string;
      label: string;
      weight: number;
    }>;
    criticalPath: {
      affected: boolean;
      explanation: string;
    };
    criticalityExplanation: string;
  }>;
  prioritization: {
    summary: {
      criticalNow: number;
      highPriority: number;
      attentionNeeded: number;
    };
    rankedBlockers: Array<{
      reasonCode: string;
      reason: string;
      priorityLevel: 'LOW' | 'ATTENTION' | 'HIGH' | 'CRITICAL' | string;
      priorityLabel: string;
      urgencyScore: number;
      criticalityExplanation: string;
      owner: string;
      nextAction: string;
      operationalImpact: Array<{
        type: string;
        label: string;
        weight: number;
      }>;
      criticalPath: {
        affected: boolean;
        explanation: string;
      };
    }>;
  };
  coordination: {
    actionOrdering: Array<{
      step: number;
      title: string;
      owner: string;
      nextAction: string;
      expectedEffect: string;
      affects: string[];
      pressureReliefScore: number;
    }>;
    queueReduction: Array<{
      queue: string;
      reason: string;
      targetCount: number;
      expectedRelief: string;
      owner: string;
      sequence: number;
    }>;
    crossRoleVisibility: Array<{
      workflow: string;
      blockedBy: string;
      overloadedDepartment: string;
      unblockAction: string;
    }>;
  };
  kpiReliability: Array<{
    key: string;
    label: string;
    reliability: 'RELIABLE' | 'PARTIAL' | 'DANGEROUS' | string;
    reason: string;
  }>;
};

export const managementApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGovernanceOverview: builder.query<GovernanceOverviewResponse, void>({
      query: () => '/system/governance-overview',
      transformResponse: (response: { success: boolean; data: GovernanceOverviewResponse }) =>
        response?.data,
      providesTags: ['Workspace'],
    }),
  }),
});

export const { useGetGovernanceOverviewQuery } = managementApi;

